import { useMemo } from "react";
import { FileText } from "lucide-react";
import { SectionCard, SectionHeader, KpiChip, ChartCard, MiniPie, MiniBar, inr, pctOf, monthKey, last6Months, SECTION_ACCENTS } from "./dashboardUi";

const STATUS_LABEL = {
  draft: "Draft", sent: "Sent", customer_evaluation_wip: "Customer Review", verbally_confirmed_accepted: "Verbally Accepted",
  reprocessing_required: "Reprocessing", accepted: "Accepted", rejected: "Rejected",
};
const C = SECTION_ACCENTS.quotation.chart;

export default function QuotationSection({ quotations, delay }) {
  const insights = useMemo(() => {
    const statusCounts = {};
    let acceptedValue = 0;
    const byMonth = {};
    for (const q of quotations) {
      statusCounts[q.status] = (statusCounts[q.status] || 0) + 1;
      if (q.status === "accepted") acceptedValue += Number(q.grand_total || 0);
      const k = monthKey(q.created_at);
      if (k) byMonth[k] = (byMonth[k] || 0) + 1;
    }
    const sentOnwards = quotations.filter((q) => q.status !== "draft").length;

    return {
      total: quotations.length,
      sent: statusCounts.sent || 0,
      accepted: statusCounts.accepted || 0,
      rejected: statusCounts.rejected || 0,
      reprocessing: statusCounts.reprocessing_required || 0,
      conversionPct: pctOf(statusCounts.accepted || 0, sentOnwards),
      acceptedValue,
      statusData: Object.entries(statusCounts).map(([k, value]) => ({ name: STATUS_LABEL[k] || k, value })),
      trendData: last6Months(byMonth),
    };
  }, [quotations]);

  return (
    <SectionCard accent="quotation" delay={delay} testid="dashboard-quotation-section">
      <SectionHeader icon={FileText} accent="quotation" title="Quotations" subtitle="Quote funnel and conversion to Accepted" />
      <div className="flex items-center gap-1 flex-wrap mb-3">
        <KpiChip label="Total Quotations" value={insights.total} tone="bg-[#6D28D9] text-white" to="/crm/quotations" />
        <KpiChip label="Sent" value={insights.sent} tone="bg-sky-50 text-sky-800" to="/crm/quotations" />
        <KpiChip label="Accepted" value={insights.accepted} tone="bg-emerald-50 text-emerald-800" to="/crm/quotations" />
        <KpiChip label="Rejected" value={insights.rejected} tone="bg-red-50 text-red-700" to="/crm/quotations" />
        <KpiChip label="Reprocessing" value={insights.reprocessing} tone="bg-amber-50 text-amber-800" to="/crm/quotations" />
        <KpiChip label="Conversion Rate" value={`${insights.conversionPct}%`} to="/crm/quotations" />
        <KpiChip label="Accepted Value" value={inr(insights.acceptedValue)} to="/crm/quotations" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dashboard-quotation-charts">
        <ChartCard title="Quotation Status" testid="chart-quote-status"><MiniPie data={insights.statusData} colors={C} to="/crm/quotations" /></ChartCard>
        <ChartCard title="Quotation Volume (6mo)" testid="chart-quote-trend"><MiniBar data={insights.trendData} color={C[0]} to="/crm/quotations" /></ChartCard>
      </div>
    </SectionCard>
  );
}
